import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useUpdateDetailsMutation } from '../services/registrationApi';
import { clearPending, readPending, savePending } from './pendingRegistration';
import { useLazyGetCustomerOrgListQuery } from '../../organization-selection/services/orgSelectionApi';
import { endSession, refreshTokenFresh } from '../../../services/auth-token';
import { scopeRootOfToken } from '../../../services/entity-claim';
import { switchEntity } from '../../../services/entity-scope';
import { refreshIfRootless } from '../../../services/entity-scope-reanchor';
import { isSessionEnding } from '../../../services/switch-errors';
import { landOn } from '../../../services/navigation-port';
import { unscopedLandingRoute } from '../../../services/org-data';
import { showAlert } from '../../../slice/AlertSlice';
import { Variants } from '@/types/common';
import type { AccessibleOrg } from '../../../types/org-selection.types';
import type {
  AdditionalDetailsFormValues,
  UserResponse,
} from '../../../types/registration.types';

interface SubmitParams {
  values: AdditionalDetailsFormValues;
  userId: string | null;
  setFieldError: (field: string, message: string) => void;
}

const DUPLICATE_NAME = 'Entity with the same name already exists';

/**
 * Waits between reads of the org list while looking for an account the
 * backend may still be writing — the registration that times out at the
 * gateway is a slow one (Tarkington's took 19 s), so it can land after the
 * failure reached us. About 14 s in all, behind the spinner.
 */
export const ACCOUNT_LOOKUP_BACKOFF_MS = [2_000, 4_000, 8_000];

/**
 * The look for an account a registration may still be creating when nothing
 * has said whether it will: its answer was lost, or a reload abandoned it.
 * That request may have only just reached entity-service, and a registration
 * has taken 30 s, so this one waits longer: about a minute between reads
 * (each read, with the refresh before it, bounded by `ORG_LIST_READ_TIMEOUT_MS`).
 */
export const PATIENT_LOOKUP_BACKOFF_MS = [2_000, 4_000, 8_000, 15_000, 30_000];

/**
 * A read of the account list that has not answered by now counts as one that
 * failed. The base query sets no timeout, and the page waits behind a spinner
 * on these reads. The refresh that may precede a read
 * (`ORG_LIST_REFRESH_WAIT_MS`) comes out of the same limit.
 */
export const ORG_LIST_READ_TIMEOUT_MS = 10_000;

/**
 * The longest a read waits on the refresh before it. A refresh normally
 * answers in well under a second; one that has not by now is left running and
 * the read goes out with the token in hand, keeping the rest of
 * `ORG_LIST_READ_TIMEOUT_MS`.
 */
export const ORG_LIST_REFRESH_WAIT_MS = 3_000;

type RegistrantName = Pick<AdditionalDetailsFormValues, 'firstName' | 'lastName'>;

/**
 * A search of the user's accounts: what it found, and whether the LAST read of
 * the list succeeded — an earlier "not there" followed by failed reads is
 * "could not tell", not "no".
 */
interface AccountSearch {
  org: AccessibleOrg | null;
  answered: boolean;
}

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const withTimeout = <T>(promise: Promise<T>, ms: number, onTimeout: () => void) =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      onTimeout();
      reject(new Error(`no answer in ${ms} ms`));
    }, ms);
    promise.then(resolve, reject).finally(() => clearTimeout(timer));
  });

const sameName = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * How to recognise the account a registration created among the user's
 * accounts: by the id the registration answered with when it answered, else
 * by the name the form gave it — entity-service refuses a second account of
 * the same name (the 409 below), so the name names one account.
 */
interface AccountMatch {
  entityId?: string;
  name: string;
}

const findMatch = (orgs: AccessibleOrg[], match: AccountMatch) =>
  (match.entityId !== undefined && orgs.find((o) => o.entityId === match.entityId)) ||
  orgs.find((o) => sameName(o.name, match.name)) ||
  null;

/** The account this registration created — read already, or yet to be found. */
type Registration = { org: AccessibleOrg } | { match: AccountMatch };

/**
 * A registration that may have created an account, which the user's account
 * list could not confirm either way: its last read failed. The form is
 * withheld, since its resubmit is how a second account gets made.
 */
const UNCONFIRMED = 'unconfirmed';

/**
 * Did the registration fail without saying whether it created the account? A
 * 5xx, a 408, or no status at all (a dropped connection, a client timeout).
 * Any other 4xx is a refusal: nothing was created. A body that is not JSON —
 * an HTML error page — arrives as RTK's `PARSING_ERROR`, with the HTTP status
 * beside it as `originalStatus`; that status is the answer.
 */
const answerLost = (error: unknown) => {
  const { status, originalStatus } = (error ?? {}) as { status?: unknown; originalStatus?: unknown };
  const code =
    status === 'PARSING_ERROR' && typeof originalStatus === 'number' ? originalStatus : status;
  return typeof code !== 'number' || code >= 500 || code === 408;
};

const isDuplicateName = (error: unknown) =>
  ((error as { data?: { message?: string } })?.data?.message ?? '').includes(DUPLICATE_NAME);

export function usePostRegistrationRedirect() {
  const [isProcessing, setIsProcessing] = useState(false);
  // Set while looking patiently for an account a registration may still be
  // creating — one whose answer was lost, or that a reload abandoned — and
  // kept once it is found, while the page enters it.
  const [isSearching, setIsSearching] = useState(false);
  // Set when a registration may have created an account that could not be
  // confirmed (`UNCONFIRMED`). The form stays withheld; a reload checks again.
  const [isUnconfirmed, setIsUnconfirmed] = useState(false);
  // Set from the first line of `submit`, before any await: `isProcessing`
  // disables the button only once React re-renders, and a second submit in
  // that window would race the first — entity-service checks the name, then
  // inserts, with no unique index behind it. Cleared only when the form comes
  // back, never once an account exists or may exist unconfirmed.
  const submitting = useRef(false);
  const dispatch = useDispatch();
  const [updateDetails] = useUpdateDetailsMutation();
  // Post-registration users are always non-admin (admin onboarding doesn't
  // run through the additional-details flow), so only the customer-side
  // org-list query is needed here.
  const [triggerCustomerOrgList] = useLazyGetCustomerOrgListQuery();

  // Every exit once the account exists REPLACES this page's history entry, so
  // Back cannot return to a form that would create a second account.
  const leaveForm = (path: string) => landOn(path, undefined, { replace: true });

  const readOrgList = async (userId: string) => {
    const startedAt = Date.now();
    // A token that names no account cannot read the list once the account
    // exists: user-service answers through entity-service's byUserId read,
    // which refuses a rootless token (503 → 500). A registrant's token predates
    // the account, and a reloaded page still holds it, so mint a fresh one
    // first (`refreshIfRootless`, the rule `switchEntity` follows too). Before
    // the account exists the fresh token is rootless too and the list reads
    // empty, as it should.
    //
    // Refresh FIRST, not only after a failed read, although that would save
    // the refresh while the account does not exist yet: a refresh-on-failure
    // relies on the guard answering a rootless token with an error. Were it
    // ever to answer it as a refusal instead — a 200 with the account left out
    // — the read would succeed without the account, nothing would trigger the
    // refresh, and the search would conclude "not there" and offer the form
    // that makes a second one. One refresh per read is the price of not
    // depending on that.
    //
    // A refresh that has not answered by `ORG_LIST_REFRESH_WAIT_MS` is left,
    // and the read goes out with the token in hand — a failed read is "could
    // not tell", never "not there".
    await withTimeout(refreshIfRootless(), ORG_LIST_REFRESH_WAIT_MS, () => undefined).catch(
      () => undefined,
    );
    const read = triggerCustomerOrgList(userId);
    // Aborted on timeout, not just abandoned: RTK Query sends no new request
    // for a key while one is pending — it waits on that one — so a hung read
    // left running would hold every later attempt. The refresh above came out
    // of the same limit.
    const remaining = ORG_LIST_READ_TIMEOUT_MS - (Date.now() - startedAt);
    return withTimeout(read.unwrap(), remaining, () => read.abort());
  };

  /**
   * The user's account that `match` names, or null. Matched, never "any
   * account": a user can reach this page already holding one, and switching
   * them into it would hide a failed registration. (After a 409, an account
   * of the same name they already held IS entered: it may be their own
   * earlier submit, and either way nothing new was created and they gain no
   * access they lacked.) An account not there yet is looked for again after
   * each of `waits`. `answered` is false when the last read of the list
   * failed — "not found" then means "could not tell".
   */
  const searchAccount = async (
    userId: string | null,
    match: AccountMatch,
    waits: number[],
  ): Promise<AccountSearch> => {
    let answered = false;
    if (!userId) return { org: null, answered };
    for (let attempt = 0; ; attempt += 1) {
      try {
        const org = findMatch(await readOrgList(userId), match);
        answered = true;
        if (org) return { org, answered };
      } catch {
        // Unknown this time; the next attempt may tell.
        answered = false;
      }
      if (attempt >= waits.length) return { org: null, answered };
      await wait(waits[attempt]);
    }
  };

  const findAccount = async (userId: string | null, match: AccountMatch, waits: number[]) =>
    (await searchAccount(userId, match, waits)).org;

  /** `searchAccount` over about a minute, with the page saying what it is waiting on. */
  const searchPatiently = async (userId: string | null, name: string) => {
    setIsSearching(true);
    const search = await searchAccount(userId, { name }, PATIENT_LOOKUP_BACKOFF_MS);
    if (!search.org) setIsSearching(false);
    return search;
  };

  const enterNewAccount = async (
    userId: string | null,
    name: RegistrantName,
    registration: Registration,
  ) => {
    const rememberName = () =>
      localStorage.setItem('userName', `${name.firstName} ${name.lastName}`);
    // Written now, so a failed refresh below still leaves the header the
    // name, and again after it, because a refresh rewrites it from the
    // token's name claims.
    rememberName();
    // A token already rooted at the account being entered was minted after the
    // registration's attribute write — on a reloaded page, the read that found
    // the account refreshed to it — so another refresh would change nothing.
    // Any other root, an older account the user held included, is refreshed.
    const alreadyRooted =
      'org' in registration && scopeRootOfToken() === registration.org.entityId;
    if (!alreadyRooted) {
      try {
        // DV-3745: the tab's token was minted BEFORE the registration, so it
        // names no account. The registration has already pointed this user's
        // Keycloak attributes at the new account; adopt a token minted from
        // them — fresh, never a join onto a refresh begun before the write. The
        // new account is then the token's own root, and the switch below
        // settles it locally. (`switchEntity` makes the same refresh for any
        // rootless token, for the chooser's sake; making it here instead keeps
        // the name write after it.)
        await refreshTokenFresh();
      } finally {
        rememberName();
      }
    }
    const org =
      'org' in registration
        ? registration.org
        : await findAccount(userId, registration.match, ACCOUNT_LOOKUP_BACKOFF_MS);
    if (!org) throw new Error('the new account is not in the user\'s account list');
    // The account is in hand: the registration has answered for good.
    clearPending();
    // A fresh registrant's org is its own top-level anchor.
    await switchEntity(
      {
        entityId: org.entityId,
        entityName: org.name,
        role: org.role,
        parentEntityId: org.entityId,
      },
      'land-home',
    );
  };

  const showRegistrationError = (
    error: unknown,
    setFieldError: SubmitParams['setFieldError'],
  ) => {
    if (isDuplicateName(error)) {
      setFieldError(
        'companyName',
        'A company with the same name already exists. Please contact your administrator or support@planomatic.com for assistance.',
      );
    } else {
      dispatch(showAlert({ type: Variants.ERROR, apiResponse: error }));
    }
  };

  /**
   * The account the registration created; `UNCONFIRMED` when it may have
   * created one the account list could not confirm; or null — with the error
   * shown — when the form should come back.
   *
   * <p>A registration whose answer was lost (a 5xx, a timeout, a dropped
   * connection) may have created the account anyway, and giving the form back
   * then is how a second account gets made: so look for it, by the name the
   * form gave it, as patiently as a reloaded page does. A 409 "same name" may
   * be this user's OWN earlier registration, so look for an account of theirs
   * by that name before calling it someone else's: once, or patiently when an
   * earlier submit of that name never resolved (`earlierUnresolved`: its
   * pending record is still here) and may be the one still landing.
   */
  const register = async (
    { values, userId, setFieldError }: SubmitParams,
    earlierUnresolved: boolean,
  ): Promise<Registration | typeof UNCONFIRMED | null> => {
    try {
      const response: UserResponse | undefined = await updateDetails({ values, userId }).unwrap();
      const entityId = response?.roles?.find((r) => r.entityUuid)?.entityUuid;
      return { match: { entityId, name: values.companyName } };
    } catch (error: unknown) {
      const lost = answerLost(error);
      const patient = lost || (earlierUnresolved && isDuplicateName(error));
      if (lost || isDuplicateName(error)) {
        const { org, answered } = patient
          ? await searchPatiently(userId, values.companyName)
          : await searchAccount(userId, { name: values.companyName }, []);
        if (org) {
          console.warn('post-registration: the registration failed but its account exists', error);
          return { org };
        }
        // Only a list that answered can say the account is not there. Until
        // then the pending record stays, and the form — whose resubmit, or a
        // rename after a 409, would make a second account — is withheld.
        if (patient && !answered) {
          console.warn('post-registration: could not confirm whether the registration landed', error);
          return UNCONFIRMED;
        }
      }
      // A refusal settles it. A lost answer does not — entity-service may still
      // be creating the account — so the pending record stays: a reload in the
      // next few minutes searches for it instead of offering the form, and a
      // resubmit's 409 searches as patiently instead of asking for a rename.
      if (!lost) clearPending();
      showRegistrationError(error, setFieldError);
      return null;
    }
  };

  /** The registration's outcome is unknown: no form, and no spinner either. */
  const holdUnconfirmed = () => {
    setIsProcessing(false);
    setIsUnconfirmed(true);
  };

  /**
   * The account exists from here on. The form must never come back — a second
   * submit creates a second account (DV-3745) — so `isProcessing` stays set
   * until this page is left, and every failure below leaves it.
   */
  const openAccount = async (
    userId: string | null,
    name: RegistrantName,
    registration: Registration,
  ) => {
    // Registration-flow scratch state (SSO prefill for the form). Spent now
    // that the account exists: keeping it would only re-fill a form whose
    // submit duplicates the account.
    localStorage.removeItem('entityData');
    try {
      await enterNewAccount(userId, name, registration);
    } catch (error: unknown) {
      // The switch ended the session; its navigation is pending (DV-3421
      // review round 5, item 4).
      if (isSessionEnding(error)) return;
      // The account was created but could not be opened. The account chooser
      // lists it, and choosing it runs the switch again.
      console.warn('post-registration: could not open the new account', error);
      dispatch(
        showAlert({
          type: Variants.WARNING,
          message: 'Your account has been created. Select it to continue.',
        }),
      );
      leaveForm(unscopedLandingRoute());
    }
  };

  const submit = async (params: SubmitParams) => {
    if (submitting.current) return;
    submitting.current = true;
    const { values, userId } = params;
    setIsProcessing(true);
    let earlierUnresolved = false;
    if (userId) {
      const earlier = readPending(userId);
      earlierUnresolved = earlier !== null && sameName(earlier.companyName, values.companyName);
      savePending(userId, values);
    }
    const registration = await register(params, earlierUnresolved);
    if (registration === UNCONFIRMED) {
      holdUnconfirmed();
      return;
    }
    if (!registration) {
      // Nothing was created, so the form comes back — kept mounted behind the
      // spinner, with what was typed and the field error — for a correction.
      submitting.current = false;
      setIsProcessing(false);
      return;
    }
    await openAccount(userId, values, registration);
  };

  /**
   * Run as the page loads, before the form is offered. True when the form is
   * withheld — the page is being left, or a registration it may have created
   * could not be confirmed (`isUnconfirmed`) — false when it should be shown.
   *
   * <p>DV-3745 follow-up. This page creates an account, so it must not offer
   * the form to a user who has one, or may be about to:
   * <ul>
   *   <li>A registration this browser sent and never heard back from — the
   *       page was reloaded mid-request — is looked for patiently, by the
   *       company name it sent, and entered when found. Showing the form
   *       instead is how a reload became a second account: the prefill is
   *       still there, the resubmit's 409 can arrive before the first account
   *       is in the user's list, and the user renames the company.</li>
   *   <li>Otherwise, a user who already holds an account is sent to the
   *       account chooser. The app only routes users with no account here,
   *       so this is a typed or stale URL.</li>
   * </ul>
   * Without a pending record, not knowing reads as "no account": the form is
   * shown, as before.
   */
  const resume = async (userId: string | null): Promise<boolean> => {
    if (!userId || submitting.current) return false;
    const pending = readPending(userId);
    if (pending) {
      submitting.current = true;
      setIsProcessing(true);
      const { org, answered } = await searchPatiently(userId, pending.companyName);
      if (org) {
        await openAccount(userId, pending, { org });
        return true;
      }
      // When the last read of the list failed, "could not tell" is not "no":
      // the pending record stays, the form is withheld, and the next load
      // searches again.
      if (!answered) {
        console.warn('post-registration: could not check on a pending registration');
        holdUnconfirmed();
        return true;
      }
      // It never landed: the form comes back, prefilled as before.
      clearPending();
      submitting.current = false;
      setIsProcessing(false);
      return false;
    }
    try {
      // Enabled accounts only — the bootstrap's own rule (`resolveEntityScope`).
      // A user whose only account is disabled would find nothing to choose.
      if ((await readOrgList(userId)).some((org) => !org.disabled)) {
        leaveForm(unscopedLandingRoute());
        return true;
      }
    } catch {
      // Unknown: offer the form, as before this check existed.
    }
    return false;
  };

  return { submit, resume, endSession, isProcessing, isSearching, isUnconfirmed };
}
